import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardDataTableSortButton,
  TablePagePagination,
  DefinitionTooltip,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { type MetaAdsPaginationMeta } from "src/services/MetaAdsDashboardAPI";
import {
  EMPTY_API_VALUE,
  formatOptionalNumber,
  getMetaColumnTooltip,
  getRoasToneClass,
  type AdSortKey,
  type LiveAdRow,
} from "./metaTableConfig";
import { formatPaidMediaCount, formatPaidMediaPercent, formatPaidMediaRoas } from "../../utils/paidMediaNumberFormat";
import { formatOverviewDateLabel } from "../../utils/paidMediaOverviewFormat";

interface MetaAdsTableProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  adsPaginationMeta: MetaAdsPaginationMeta;
  searchAds: string;
  setSearchAds: Dispatch<SetStateAction<string>>;
  setCurrentAdsPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportAdsCsv: () => Promise<void>;
  toggleAdsSort: (_key: AdSortKey) => void;
  paginatedAds: LiveAdRow[];
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  formatMoneyShort: (_value: number) => string;
  currentAdsPage: number;
  adsTotalPages: number;
}

const MetaAdsTable: FC<MetaAdsTableProps> = ({
  metaSectionHelp,
  adsPaginationMeta,
  searchAds,
  setSearchAds,
  setCurrentAdsPage,
  isExporting,
  exportAdsCsv,
  toggleAdsSort,
  paginatedAds,
  formatMoney,
  formatMoneyShort,
  currentAdsPage,
  adsTotalPages,
}) => {
  const { t } = useTranslation();

  return (
                <DashboardDataTablePanel className="paid-media-table-panel--attached paid-media-ads-table-panel">
                  <DashboardDataTablePanelHeader>
                    <div className="paid-media-table-panel__heading-block">
                      <div className="paid-media-panel__heading">
                        <h3>{t("metaAdsReport.sections.allAdsTitle", { count: adsPaginationMeta.total_items })}</h3>
                        <DefinitionTooltip
                          label={t("metaAdsReport.overviewSubTabs.ads")}
                          info={metaSectionHelp("allAds")}
                        />
                      </div>
                      <p className="paid-media-table-panel__subtitle">
                        {t("metaAdsReport.sections.adsSubtitle", {
                          count: adsPaginationMeta.total_items,
                        })}
                      </p>
                    </div>
                    <div
                      className="paid-media-table-toolbar"
                      role="toolbar"
                      aria-label={t("metaAdsReport.toolbars.adsSearch")}
                    >
                      <label className={`${dt.search} paid-media-table-toolbar__search`}>
                        <Search size={16} />
                        <input
                          type="search"
                          value={searchAds}
                          onChange={event => {
                            setSearchAds(event.target.value);
                            setCurrentAdsPage(1);
                          }}
                          placeholder={t("metaAdsReport.placeholders.searchAds")}
                        />
                      </label>
                      <button
                        type="button"
                        className="paid-media-export-btn"
                        disabled={isExporting}
                        onClick={() => void exportAdsCsv()}
                      >
                        <Download size={16} aria-hidden />
                        {isExporting
                          ? t("metaAdsReport.export.exporting")
                          : t("metaAdsReport.export.csv")}
                      </button>
                    </div>
                  </DashboardDataTablePanelHeader>
                  <DashboardDataTableScroll>
                    <DashboardDataTableTable className="paid-media-ads-performance-table">
                      <thead>
                        <tr>
                          <th className="paid-media-report__ad-col">
                            <DashboardDataTableSortButton
                              onClick={() => toggleAdsSort("name")}
                            >
                              {t("metaAdsReport.campaignTable.campaign")}
                            </DashboardDataTableSortButton>
                          </th>
                          {(
                            [
                              "dailyBudget",
                              "linkClicks",
                              "createdAtDate",
                              "createdAtTime",
                            ] as const
                          ).map(column => (
                            <th key={column} className={dt.number}>
                              <span className={dt.thHelpRow}>
                                {t(`metaAdsReport.adsTable.${column}`)}
                                <DefinitionTooltip
                                  label={t(`metaAdsReport.adsTable.${column}`)}
                                  info={getMetaColumnTooltip(t, column, "ad")}
                                  formulaTitle={t("webAnalytics.kpi.formula")}
                                />
                              </span>
                            </th>
                          ))}
                          {(
                            [
                              ["spend", dt.number],
                              ["impressions", ""],
                              ["conversions", ""],
                              ["ctr", ""],
                              ["cpc", ""],
                              ["cpa", ""],
                              ["roas", ""],
                            ] as const
                          ).map(([column, alignClass], columnIndex, columns) => (
                            <th key={column} className={alignClass || undefined}>
                              <span className={dt.thHelpRow}>
                                <DashboardDataTableSortButton
                                  onClick={() => toggleAdsSort(column)}
                                >
                                  {t(`metaAdsReport.adsTable.${column}`)}
                                </DashboardDataTableSortButton>
                                <DefinitionTooltip
                                  label={t(`metaAdsReport.adsTable.${column}`)}
                                  info={getMetaColumnTooltip(t, column, "ad")}
                                  align={
                                    columnIndex >= columns.length - 3 ? "end" : "start"
                                  }
                                  formulaTitle={t("webAnalytics.kpi.formula")}
                                />
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAds.map(row => (
                          <tr key={row.id}>
                            <td className="paid-media-report__ad-col">
                              <strong className="paid-media-table__entity-link">{row.name}</strong>
                            </td>
                            <td className={dt.number}>{formatOptionalNumber(row.dailyBudget, formatMoney)}</td>
                            <td className={dt.number}>{formatOptionalNumber(row.linkClicks)}</td>
                            <td className={dt.number}>{row.createdAtDate ? formatOverviewDateLabel(row.createdAtDate) : EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{row.createdAtTime ? formatOverviewDateLabel(row.createdAtTime) : EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{formatMoney(row.spend)}</td>
                            <td>{formatPaidMediaCount(row.impressions)}</td>
                            <td>{formatPaidMediaCount(row.conversions)}</td>
                            <td>{formatPaidMediaPercent(row.ctr)}</td>
                            <td>{formatMoneyShort(row.cpc)}</td>
                            <td>{formatMoneyShort(row.cpa)}</td>
                            <td className={getRoasToneClass(row.roas)}>{formatPaidMediaRoas(row.roas)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DashboardDataTableTable>
                  </DashboardDataTableScroll>
                  <TablePagePagination
                    currentPage={currentAdsPage}
                    totalPages={adsTotalPages}
                    onPrevious={() =>
                      setCurrentAdsPage(page => Math.max(1, page - 1))
                    }
                    onNext={() =>
                      setCurrentAdsPage(page =>
                        Math.min(adsTotalPages, page + 1)
                      )
                    }
                />
              </DashboardDataTablePanel>
  );
};

export default MetaAdsTable;
